import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { ReadableNote } from '../components/ReadableNote';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Moon,
  Battery,
  Activity,
  Brain,
  Smile,
  CheckCircle,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Edit2,
} from 'lucide-react';
import { toast } from 'sonner';

const METRICS = [
  {
    key: 'sleep_score',
    label: 'Sleep Quality',
    labelIT: 'Qualità del Sonno',
    icon: Moon,
    color: 'text-indigo-400',
    bgColor: 'bg-indigo-500/10',
    borderColor: 'border-indigo-500/30',
    options: [
      { value: 1, label: 'Very Poor', labelIT: 'Pessimo' },
      { value: 2, label: 'Poor', labelIT: 'Scarso' },
      { value: 3, label: 'Average', labelIT: 'Medio' },
      { value: 4, label: 'Good', labelIT: 'Buono' },
      { value: 5, label: 'Excellent', labelIT: 'Eccellente' },
    ],
    higherBetter: true,
  },
  {
    key: 'fatigue_score',
    label: 'Fatigue Level',
    labelIT: 'Livello di Fatica',
    icon: Battery,
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/10',
    borderColor: 'border-amber-500/30',
    options: [
      { value: 1, label: 'Very Fresh', labelIT: 'Molto Fresco' },
      { value: 2, label: 'Fresh', labelIT: 'Fresco' },
      { value: 3, label: 'Normal', labelIT: 'Normale' },
      { value: 4, label: 'Fatigued', labelIT: 'Affaticato' },
      { value: 5, label: 'Very Fatigued', labelIT: 'Molto Affaticato' },
    ],
    higherBetter: false,
  },
  {
    key: 'soreness_score',
    label: 'Muscle Soreness',
    labelIT: 'Dolori Muscolari',
    icon: Activity,
    color: 'text-rose-400',
    bgColor: 'bg-rose-500/10',
    borderColor: 'border-rose-500/30',
    options: [
      { value: 1, label: 'None', labelIT: 'Nessuno' },
      { value: 2, label: 'Mild', labelIT: 'Leggero' },
      { value: 3, label: 'Moderate', labelIT: 'Moderato' },
      { value: 4, label: 'Significant', labelIT: 'Significativo' },
      { value: 5, label: 'Severe', labelIT: 'Severo' },
    ],
    higherBetter: false,
  },
  {
    key: 'stress_score',
    label: 'Stress Level',
    labelIT: 'Livello di Stress',
    icon: Brain,
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/30',
    options: [
      { value: 1, label: 'Very Relaxed', labelIT: 'Molto Rilassato' },
      { value: 2, label: 'Relaxed', labelIT: 'Rilassato' },
      { value: 3, label: 'Normal', labelIT: 'Normale' },
      { value: 4, label: 'Stressed', labelIT: 'Stressato' },
      { value: 5, label: 'Very Stressed', labelIT: 'Molto Stressato' },
    ],
    higherBetter: false,
  },
  {
    key: 'mood_score',
    label: 'Mood',
    labelIT: 'Umore',
    icon: Smile,
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500/30',
    options: [
      { value: 1, label: 'Very Bad', labelIT: 'Pessimo' },
      { value: 2, label: 'Bad', labelIT: 'Cattivo' },
      { value: 3, label: 'Neutral', labelIT: 'Neutro' },
      { value: 4, label: 'Good', labelIT: 'Buono' },
      { value: 5, label: 'Excellent', labelIT: 'Eccellente' },
    ],
    higherBetter: true,
  },
];

const getReadinessColor = (score) => {
  if (score >= 60) return 'text-emerald-400';
  if (score >= 40) return 'text-amber-400';
  return 'text-rose-400';
};

const getAlertBadge = (level) => {
  if (level === 'GREEN') return { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Good' };
  if (level === 'AMBER') return { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'Alert' };
  return { bg: 'bg-rose-500/20', text: 'text-rose-400', label: 'Warning' };
};

const AthleteWellnessPage = () => {
  const { api } = useAuth();
  const { t, language } = useLanguage();

  const [todayEntry, setTodayEntry] = useState(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    sleep_score: 3,
    fatigue_score: 3,
    soreness_score: 3,
    stress_score: 3,
    mood_score: 3,
    notes: '',
  });

  const fetchTodayWellness = useCallback(async () => {
    try {
      const response = await api.get('/wellness/today');
      if (response.data.entry) {
        setTodayEntry(response.data.entry);
        setHasSubmitted(true);
        setFormData({
          sleep_score: response.data.entry.sleep_score,
          fatigue_score: response.data.entry.fatigue_score,
          soreness_score: response.data.entry.soreness_score,
          stress_score: response.data.entry.stress_score,
          mood_score: response.data.entry.mood_score,
          notes: response.data.entry.notes || '',
        });
      }
    } catch (err) {
      console.error('Failed to fetch today wellness:', err);
    }
  }, [api]);

  const fetchHistory = useCallback(async () => {
    try {
      // Get athlete's own history (the endpoint figures out athlete_id from token)
      const meResponse = await api.get('/auth/me');
      if (meResponse.data.athlete_id) {
        const response = await api.get(`/wellness/athlete/${meResponse.data.athlete_id}?limit=14`);
        setHistory(response.data.entries || []);
      }
    } catch (err) {
      console.error('Failed to fetch history:', err);
    }
  }, [api]);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      await fetchTodayWellness();
      await fetchHistory();
      setIsLoading(false);
    };
    load();
  }, [fetchTodayWellness, fetchHistory]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (hasSubmitted && todayEntry) {
        // Update existing
        await api.put(`/wellness/${todayEntry.id}`, formData);
        toast.success(language === 'IT' ? 'Wellness aggiornato!' : 'Wellness updated!');
      } else {
        // Create new
        await api.post('/wellness', formData);
        toast.success(language === 'IT' ? 'Wellness inviato!' : 'Wellness submitted!');
      }
      setIsEditing(false);
      await fetchTodayWellness();
      await fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleScoreChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const today = new Date().toLocaleDateString(language === 'IT' ? 'it-IT' : 'en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-zinc-500">{t('loading')}</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6 px-3 sm:p-4 lg:p-6 pb-4 fade-in">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-zinc-100">
          {language === 'IT' ? 'Il Tuo Wellness' : 'Your Wellness'}
        </h1>
        <p className="text-sm text-zinc-500 mt-1">{today}</p>
      </div>

      {/* Today's Status */}
      {hasSubmitted && !isEditing ? (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-zinc-100 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  {language === 'IT' ? 'Già Inviato Oggi' : 'Submitted Today'}
                </CardTitle>
                <CardDescription className="text-zinc-500 mt-1">
                  {language === 'IT' ? 'Puoi modificare la tua risposta' : 'You can edit your response'}
                </CardDescription>
              </div>
              <Button
                onClick={() => setIsEditing(true)}
                variant="outline"
                size="sm"
                className="border-zinc-700 text-zinc-300"
                data-testid="edit-wellness-btn"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                {t('edit')}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {/* Readiness Score */}
            <div className="text-center py-4 sm:py-6 border-b border-zinc-800 mb-4 sm:mb-6">
              <div className="text-xs text-zinc-500 uppercase tracking-wider mb-2">
                {language === 'IT' ? 'Punteggio Readiness' : 'Readiness Score'}
              </div>
              <div className={`text-4xl sm:text-5xl font-bold ${getReadinessColor(todayEntry?.readiness_score || 0)}`}>
                {todayEntry?.readiness_score || 0}
              </div>
              <div className="mt-2">
                {todayEntry?.alert_level && (
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getAlertBadge(todayEntry.alert_level).bg} ${getAlertBadge(todayEntry.alert_level).text}`}>
                    {getAlertBadge(todayEntry.alert_level).label}
                  </span>
                )}
              </div>
            </div>

            {/* Metrics Display */}
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 sm:gap-4">
              {METRICS.map((metric) => {
                const Icon = metric.icon;
                const value = todayEntry?.[metric.key] || 0;
                const optionLabel = metric.options.find((o) => o.value === value);
                return (
                  <div
                    key={metric.key}
                    className={`p-2.5 sm:p-3 rounded-lg border ${metric.borderColor} ${metric.bgColor} text-center`}
                  >
                    <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${metric.color} mx-auto mb-1`} />
                    <div className="text-base sm:text-lg font-bold text-zinc-100">{value}</div>
                    <div className="text-[9px] sm:text-xs text-zinc-400 truncate">
                      {language === 'IT' ? optionLabel?.labelIT : optionLabel?.label}
                    </div>
                  </div>
                );
              })}
            </div>

            {todayEntry?.notes && (
              <div className="mt-4 p-3 bg-zinc-950/50 rounded-sm border border-zinc-800">
                <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">
                  {language === 'IT' ? 'Note' : 'Notes'}
                </div>
                <ReadableNote testId="wellness-today-note">{todayEntry.notes}</ReadableNote>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        /* Wellness Form */
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-zinc-100">
              {hasSubmitted
                ? language === 'IT' ? 'Modifica Wellness' : 'Edit Wellness'
                : language === 'IT' ? 'Come Ti Senti Oggi?' : 'How Do You Feel Today?'}
            </CardTitle>
            <CardDescription className="text-zinc-500">
              {language === 'IT'
                ? 'Seleziona un valore per ogni metrica'
                : 'Select a value for each metric'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {METRICS.map((metric) => {
                const Icon = metric.icon;
                return (
                  <div key={metric.key} className="space-y-2.5">
                    <Label className="flex items-center gap-2 text-zinc-300">
                      <Icon className={`w-4 h-4 ${metric.color}`} />
                      {language === 'IT' ? metric.labelIT : metric.label}
                    </Label>
                    <div className="grid grid-cols-5 gap-1.5 sm:gap-2">
                      {metric.options.map((option) => {
                        const isSelected = formData[metric.key] === option.value;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => handleScoreChange(metric.key, option.value)}
                            className={`py-3 sm:py-3 px-1 rounded-lg border transition-all text-center min-w-0 ${
                              isSelected
                                ? `${metric.borderColor} ${metric.bgColor} ${metric.color} ring-1 ring-current`
                                : 'border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-zinc-600 active:bg-zinc-800'
                            }`}
                            data-testid={`${metric.key}-${option.value}`}
                          >
                            <div className="text-base sm:text-lg font-bold leading-none">{option.value}</div>
                            <div className="text-[9px] sm:text-[10px] mt-1 leading-tight truncate px-0.5">
                              {language === 'IT' ? option.labelIT : option.label}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              <div className="space-y-2">
                <Label className="text-zinc-300">
                  {language === 'IT' ? 'Note (opzionale)' : 'Notes (optional)'}
                </Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                  placeholder={language === 'IT' ? 'Aggiungi note...' : 'Add notes...'}
                  className="bg-zinc-950 border-zinc-800 min-h-[80px]"
                  data-testid="wellness-notes"
                />
              </div>

              <div className="flex gap-3 pt-4 pb-2 sticky bottom-0 bg-zinc-900/95 backdrop-blur-sm -mx-6 px-6 border-t border-zinc-800/50">
                {isEditing && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsEditing(false)}
                    className="border-zinc-700 text-zinc-300"
                  >
                    {t('cancel')}
                  </Button>
                )}
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 bg-amber-500 text-zinc-950 hover:bg-amber-400 font-bold h-12 text-base"
                  data-testid="submit-wellness-btn"
                >
                  {isSubmitting
                    ? t('loading')
                    : hasSubmitted
                    ? language === 'IT' ? 'Aggiorna' : 'Update'
                    : language === 'IT' ? 'Invia' : 'Submit'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* History */}
      {history.length > 0 && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-zinc-100">
              {language === 'IT' ? 'Storico' : 'History'}
            </CardTitle>
            <CardDescription className="text-zinc-500">
              {language === 'IT' ? 'Le tue risposte recenti' : 'Your recent submissions'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {history.map((entry) => {
                const badge = getAlertBadge(entry.alert_level);
                return (
                  <div
                    key={entry.id}
                    className="flex flex-wrap items-center justify-between gap-2 min-w-0 p-2.5 sm:p-3 bg-zinc-950/50 rounded-lg border border-zinc-800"
                  >
                    <div className="flex items-center gap-3 sm:gap-4">
                      <div className="text-xs sm:text-sm text-zinc-400">{entry.date}</div>
                      <div className={`text-base sm:text-lg font-bold ${getReadinessColor(entry.readiness_score)}`}>
                        {entry.readiness_score}
                      </div>
                    </div>
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${badge.bg} ${badge.text}`}>
                      {badge.label}
                    </span>
                    <ReadableNote testId={`athlete-wellness-note-${entry.id}`}>{entry.notes}</ReadableNote>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AthleteWellnessPage;
